import { useRef, useState, useEffect, useCallback } from "react"
import { OverlayPanel } from "primereact/overlaypanel"
import { classNames } from "primereact/utils"
import { Accordion, AccordionTab } from "primereact/accordion"
import { useFormikContext, FieldArray } from "formik"
import _ from "lodash"
import * as Yup from "yup"

import { getValidationShapeForField } from "../../../helpers/validationSchema"
import MFBuilder from "./MFBuilder"
import CustomButton from "../../Buttons/CustomButton"

import styles from "../../../styles/Form-MultiBlock.module.css"

const actionDelay = 250 // ms

const NestedOverlay = ({ children }) => {
  const op = useRef(null)

  return (
    <div>
      <CustomButton
        label="View Details"
        icon="pi pi-eye"
        outlined
        className={styles.inputActionButton}
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op}>
        {children}
      </OverlayPanel>
    </div>
  )
}

export default function TypeMultiField({ field, index }) {
  const { field_name, field_label, multi_fields } = field
  const formik = useFormikContext()
  const [editMode, setEditMode] = useState(null)
  const [accordionIndex, setAccordionIndex] = useState(null) // control Accordion programmatically (null = closed)
  const scrollTopRef = useRef(null) // ref to the accordion container so we can scroll it into view
  const tmpFieldName = `${field_name}_tmp`

  const mfDefinition = _.reduce(
    multi_fields,
    (acc, field) => {
      const { field_type, field_name, multi_fields } = field
      if (field_type === "multi_field_nested") {
        const nestedField = _.reduce(
          multi_fields,
          (acc, { field_name }) => {
            return {
              ...acc,
              [field_name]: "",
            }
          },
          {}
        )
        return {
          ...acc,
          [field_name]: [nestedField],
        }
      }
      return {
        ...acc,
        [field_name]: "",
      }
    },
    {
      // Add date_created to the definition
      date_created: "",
    }
  )

  const resetTempField = useCallback(() => {
    formik.setFieldValue(tmpFieldName, [mfDefinition])
  }, [formik, tmpFieldName, mfDefinition])

  useEffect(() => {
    if (!formik.values[tmpFieldName]) {
      resetTempField()
    }
  }, [formik.values, tmpFieldName, resetTempField])

  // Extracts a display value from an object by checking for common properties.
  const getDisplayValueFromObject = (item) => {
    if (!item) return null
    const displayKeys = [
      // Generic options
      "name",
      "name2",
      "label",
      "text",
      "title",
      "value",
      "description",
      "display",
      "displayName",
      "friendlyName",
      "optionName",
      // Special options
      "column",
      "day",
      "enterprise_name",
      "fullName",
      "house",
      "location_name",
      "organization_name",
      "program_id_name",
      "program_name",
      "staff_full_name",
      "staff_name",
      "template_title",
    ]

    for (const key of displayKeys) {
      if (item[key] != null) {
        return item[key]
      }
    }
    return null
  }

  // Formats the value for display based on the field type and value structure.
  function formatDisplayValue(value, config) {
    // Handle "checkbox" field type.
    if (config.field_type === "checkbox") {
      if (config.field_options && Array.isArray(config.field_options)) {
        const selectedValues = Array.isArray(value) ? value : []
        return config.field_options.map((option, i) => {
          const isSelected = selectedValues.includes(option.value)
          return (
            <div key={i} className={styles.fieldValue} >
              <i
                className={classNames("pi", { "pi-check": isSelected, "pi-times": !isSelected }, styles.fieldValueIcon)}
              />
              {option.label}
            </div>
          )
        })
      }

      // Handle checkbox values that are an array of primitive values.
      if (Array.isArray(value)) {
        return value.map((item, i) => (
          <div key={i} className={styles.fieldValue}>
            <i className={classNames("pi pi-check", styles.fieldValueIcon)} />
            {item}
          </div>
        ))
      }
    }

    // Handle array of objects.
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && value[0] !== null) {
      return value.map((item, i) => {
        // Attempt to get a user-friendly display value.
        const displayValue = getDisplayValueFromObject(item)
        if (displayValue) {
          return <div key={i}>{displayValue}</div>
        }
        // Fallback for objects without a specific display property
        return (
          <pre key={i} className={styles.preWrap}>
            {JSON.stringify(item, null, 2)}
          </pre>
        )
      })
    }

    // Handle single plain object.
    if (value && typeof value === "object" && value !== null && !Array.isArray(value)) {
      // Attempt to get a user-friendly display value.
      const displayValue = getDisplayValueFromObject(value)
      if (displayValue) {
        return <div>{displayValue}</div>
      }
      // Fallback for objects without a specific display property
      return (
        <pre className={styles.preWrap}>
          {JSON.stringify(value, null, 2)}
        </pre>
      )
    }

    // Handle rich text fields.
    if (config.field_type === "textarea_rich") {
      return (
        <div
          className={styles.richTextValue}
          dangerouslySetInnerHTML={{ __html: value || "--" }}
        />
      )
    }

    // Fallback to converting the value to a string if possible.
    if (value != null) {
      return value.toString() || "--"
    }

    // Default fallback display.
    return "--"
  }

  function handleSingleField(key, value, config, itemErrors) {
    const error = itemErrors?.[key]
    const valueDisplay = formatDisplayValue(value, config)

    return (
      <div key={key} className={styles.itemField}>
        <label className={styles.itemLabel}>{config.field_label}</label>
        <div className={styles.itemValue}>
          {valueDisplay}
        </div>
        <div>
          {error && <small className="p-error">{error}</small>}
        </div>
      </div>
    )
  }

  function handleNestedMF(key, value, config) {
    return (
      <div key={key} className={styles.itemField}>
        <div className={styles.itemLabel}>{config.field_label}</div>
        <NestedOverlay>
          <div className={classNames(styles.nestedContainer, styles.nestedContainerPopup)}>
            <div className={styles.head}>
              <label className={styles.blockLabel}>{config.field_label}</label>
            </div>
            {
              value.map((nestedItem, nestedIndex) =>
                handleMFBlock(nestedItem, nestedIndex, config)
              )
            }
          </div>
        </NestedOverlay>
      </div>
    )
  }

  function handleMFBlock(mfBlock, mfBlockIndex, mfConfigSource) {
    // Safeguard: Ensure mfBlock is an object before processing
    if (!_.isPlainObject(mfBlock)) {
      return null
    }

    const completeMfBlock = _.defaultsDeep({}, mfBlock, mfDefinition) // Fill in any missing fields
    const isNestedMF = mfConfigSource && mfConfigSource.field_type === "multi_field_nested"
    const effectiveConfigSource = mfConfigSource || field
    const mfItemConfigMap = _.keyBy(effectiveConfigSource.multi_fields, "field_name")
    const itemErrors = formik.errors[field_name]?.[mfBlockIndex] || {}

    // Sort fields based on mfDefinition for consistent order
    const definitionOrderMap = Object.fromEntries(Object.keys(mfDefinition).map((k, i) => [k, i]))
    const sortedBlockKeys = Object.keys(completeMfBlock).sort(
      (a, b) => (definitionOrderMap[a] ?? Infinity) - (definitionOrderMap[b] ?? Infinity)
    )

    return (
      <div key={`${field_name}_${mfBlockIndex}`} className={classNames(styles.fieldContainer)}>
        {sortedBlockKeys.map((blockItemKey) => {
          const mfItemKey = blockItemKey
          const mfItem = completeMfBlock[blockItemKey]

          // Try to find field configuration
          const mfItemConfig = mfItemConfigMap[mfItemKey]

          // Exclude from rendering if no field configuration found
          if (!mfItemConfig) return null

          if (mfItemConfig.field_type !== "multi_field_nested") {
            // Render simple field
            return handleSingleField(mfItemKey, mfItem, mfItemConfig, itemErrors)
          }
          else {
            // Render nested multi-field array
            return handleNestedMF(mfItemKey, mfItem, mfItemConfig)
          }
        })}

        {!isNestedMF && (
          <div className={styles.itemActions}>
            <>
              <CustomButton
                label="Edit"
                icon="pi pi-pencil"
                className={styles.inputActionButton}
                onClick={() => handleEdit(mfBlock, mfBlockIndex)}
                size="small"
              />
              <CustomButton
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                className={styles.inputActionButton}
                onClick={() => handleDelete(mfBlock, mfBlockIndex)}
                size="small"
              />
            </>
          </div>
        )}
      </div>
    )
  }

  function scrollToRef() {
    const elementRef = scrollTopRef.current
    if (elementRef && typeof elementRef.scrollIntoView === "function") {
      setTimeout(() => {
        elementRef.scrollIntoView({ behavior: "smooth", block: "start" })
      }, actionDelay)
    }
  }

  function handleEdit(mfBlock, mfBlockIndex) {
    // Set edit mode to the index of the item being edited
    setEditMode(mfBlockIndex)

    // Populate the temporary field with the selected block's data
    formik.setFieldValue(tmpFieldName, [_.cloneDeep(mfBlock)])

    setAccordionIndex(0)
    scrollToRef()
  }

  async function handleSave() {
    // Fill in any missing fields
    const tempValues = _.defaultsDeep({}, formik.values[tmpFieldName][0], mfDefinition)
    const itemIndex = editMode // index of the item being edited

    // Build a validation schema for the temporary field
    const tempFieldSchema = Yup.object().shape(getValidationShapeForField(field))

    try {
      // Validate the temporary field's value
      await tempFieldSchema.validate(tempValues, { abortEarly: false })
      // Clear any previous errors for the temp field
      const newErrors = _.omit(formik.errors, tmpFieldName)
      formik.setErrors(newErrors)
    } catch (err) {
      // There are validation errors, do not proceed with save
      const errors = err.inner.reduce((acc, error) => {
        _.set(acc, error.path, error.message)
        return acc
      }, {})
      formik.setFieldError(tmpFieldName, [errors])
      return
    }

    if (editMode !== null) {
      // Save changes to existing item in the multi-field array
      const newFieldValues = [...formik.values[field_name]]
      newFieldValues[itemIndex] = tempValues

      // Update the existing item
      formik.setFieldValue(field_name, newFieldValues)
    } else {
      // Add new item to the multi-field array
      const newFieldValues = [
        ...formik.values[field_name],
        { ...tempValues, date_created: new Date().toISOString() },
      ]

      // Update the multi-field array with the new item
      formik.setFieldValue(field_name, newFieldValues)
    }

    // Reset the temporary field
    resetTempField()

    // close the accordion when saving/adding
    setTimeout(() => {
      setAccordionIndex(null)
    }, actionDelay)

    setEditMode(null)
  }

  function handleCancel() {
    // Reset the temporary field
    resetTempField()

    // Release edit mode
    setEditMode(null)

    // Close accordion
    setTimeout(() => {
      setAccordionIndex(null)
      scrollToRef()
    }, actionDelay)
  }

  function handleDelete(mfBlock, mfBlockIndex) {
    const fieldValues = formik.values[field_name]
    const updatedValues = fieldValues.filter((_, i) => i !== mfBlockIndex)
    formik.setFieldValue(field_name, updatedValues)
  }

  return (
    <div ref={scrollTopRef} className={styles.multiBlock} style={{ order: index }}>
      <div className={styles.head}>
        <label className={styles.blockLabel}>{field_label}</label>
      </div>

      <div className={styles.body}>
        <Accordion
          className={styles.inputContainer}
          activeIndex={accordionIndex}
          onTabChange={(e) => setAccordionIndex(e.index)}
        >
          <AccordionTab header={editMode !== null ? "Edit Item" : "Add New Item"}>
            {/* Render multi-field add/edit inputs */}
            <FieldArray
              name={field_name}
              render={(arrayHelpers) =>
                formik.values[tmpFieldName].map((value, idx) => (
                  <MFBuilder
                    arrayHelpers={arrayHelpers}
                    fields={multi_fields}
                    key={`${field_name}-${idx}`}
                    mfDefinition={mfDefinition}
                    mfFieldName={tmpFieldName}
                    mfLength={formik.values[field_name].length}
                    mfIndex={idx}
                  />
                ))
              }
            />
            <div className={styles.accordionActions}>
              <CustomButton
                className={styles.inputActionButton}
                label="Save"
                icon="pi pi-check"
                onClick={handleSave}
                size="small"
              />
              <CustomButton
                className={styles.inputActionButton}
                label="Cancel"
                icon="pi pi-times"
                severity="danger"
                onClick={handleCancel}
                size="small"
              />
            </div>
          </AccordionTab>
        </Accordion>

        <div className={styles.listContainer}>
          {/* Safeguard: Ensure formik.values[field_name] is an array before processing  */}
          {Array.isArray(formik.values[field_name]) &&
            formik.values[field_name].map((item, index) => handleMFBlock(item, index))
          }
        </div>
      </div>

    </div>
  )
}
